/**
 * Five events on one timeline, and the fifth is a refusal.
 *
 * ADR-0003 claims something weaker than a distributed lock and, unlike a
 * distributed lock, actually true of the parts involved: leases live in a store
 * that is explicitly allowed to lose data, so the guarantee cannot be "only one
 * holder exists". It is "a holder that comes back late cannot apply an effect" —
 * the protected resource keeps the highest token it has seen and refuses
 * anything lower.
 *
 * The playhead rests on the refusal, not at the end. That instant is the whole
 * argument, and under reduced motion it is where the figure sits from the first
 * frame rather than something the reader has to wait for.
 */
import { Playhead } from '../../kit'
import { useT } from '../../i18n/context'
import { FENCE, FENCE_ARGUMENT_AT } from '../../data/transcript'
import './FenceTimeline.css'

const OUTCOME_TONE = {
  granted: 'ok',
  renewed: 'ok',
  expired: 'warn',
  refused: 'err',
} as const

export function FenceTimeline() {
  const t = useT()

  return (
    <div className="kl-fence">
      <div className="kl-fence__track">
        <div className="kl-fence__rail" />
        <Playhead at={FENCE_ARGUMENT_AT} />
        {FENCE.map((e, i) => (
          <div
            key={i}
            className={`kl-fence__event kl-fence__event--${OUTCOME_TONE[e.outcome]}`}
            style={{ left: `${e.at * 100}%` }}
          >
            <span className="kl-fence__dot" aria-hidden="true" />
          </div>
        ))}
      </div>

      {/*
        The events are also a list, and the list is the accessible form of the
        figure. Positioning them along a rail is how a sighted reader reads the
        ordering; an ordered list is how everybody else does — and both are the
        same DOM, so they cannot say different things.
      */}
      <ol className="kl-fence__legend">
        {FENCE.map((e, i) => (
          <li key={i} className={`kl-fence__row kl-fence__row--${OUTCOME_TONE[e.outcome]}`}>
            <span className="k-mono kl-fence__token">fence={e.token}</span>
            <span className="k-mono kl-fence__actor">{e.actor}</span>
            <span className="kl-fence__outcome">{e.outcome}</span>
            {e.outcome === 'refused' ? (
              <span className="k-mono kl-fence__why">
                {t('continuity.fig.refused')} · {e.token} &lt; 18
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  )
}

/**
 * The four places a switch is allowed, and the five it is not.
 *
 * `domain.IsSafeBoundary` is the gate. Everything on the right is a point where
 * moving an agent loses work that cannot be reconstructed — half a tool call,
 * half a migration, half a paste.
 */
export function SafeBoundaryStrip() {
  const t = useT()
  // Left: the actual `SafeBoundary` constants from internal/domain/execution.go,
  // printed as the machine values they are and therefore not translated.
  const safe = [
    'TURN_COMPLETED',
    'CHECKPOINT_CREATED',
    'TASK_STEP_COMPLETED',
    'WAITING_FOR_HUMAN',
    'RATE_LIMIT_CONFIRMED',
    'AUTH_FAILURE_CONFIRMED',
    'MODEL_UNAVAILABLE_CONFIRMED',
    'RECOVERABLE_SESSION_FAILURE',
  ]
  // Right: not constants. There is no enumeration of unsafe points in the code —
  // the set is "everything else" — so these are described in words and set in
  // sans, because dressing prose as an identifier would invent an API.
  const unsafe = [
    'continuity.unsafe.tool',
    'continuity.unsafe.write',
    'continuity.unsafe.migration',
    'continuity.unsafe.deploy',
    'continuity.unsafe.paste',
  ] as const

  return (
    <div className="kl-bound">
      <h3 className="kl-bound__title">{t('continuity.boundaryTitle')}</h3>
      <p className="kl-bound__sub">{t('continuity.boundarySub')}</p>
      <div className="kl-bound__cols">
        <ul className="kl-bound__list kl-bound__list--safe">
          {safe.map((s) => (
            <li key={s} className="k-mono">
              {s}
            </li>
          ))}
        </ul>
        <ul className="kl-bound__list kl-bound__list--unsafe">
          {unsafe.map((key) => (
            <li key={key}>{t(key)}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}
